import { FC, PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";

import { AccordionDetails, AccordionSummary, Stack } from "@mui/material";

import { CatalogPageContext } from "@pages/Catalog/context";

import { useFiltersData } from "@hooks/useFiltersData";

import { ITags } from "@models/models";

import * as Styled from "./styles";

const ChevronIcon = () => (
  <svg width="10" height="5" viewBox="0 0 14 7" fill="none">
    <path
      d="M1 1L7 6L13 1"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type TFilterSelection = {
  title: string;
  defaultExpanded?: boolean;
  tags?: ITags[];
  onExpand?: (...any) => any;
} & PropsWithChildren;

export const FilterSelection: FC<TFilterSelection> = ({ title, children, defaultExpanded, tags, onExpand }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { handlers } = useContext(CatalogPageContext);
  const { handleSetFilters } = handlers;
  const { availableTags } = useFiltersData(handleSetFilters);

  const handleExpand = () => {
    if (onExpand) {
      onExpand();
    }
  };

  useEffect(() => {
    if (isExpanded) {
      handleExpand();
    }
  }, [isExpanded]);

  const isDisabled = useMemo(
    () => !!tags && !tags.some((tag) => availableTags && availableTags[tag._id]),
    [availableTags],
  );

  // Extract count from title (format: "Name N" where N is the count)
  const titleParts = title.match(/^(.+?)\s+(\d+)$/);
  const displayTitle = titleParts ? titleParts[1] : title;
  const count = titleParts ? titleParts[2] : null;

  return (
    <Styled.Accordion
      TransitionProps={{ unmountOnExit: true }}
      defaultExpanded={defaultExpanded}
      onChange={(_, expanded) => {
        setIsExpanded(isDisabled ? !isDisabled : expanded);
      }}
      expanded={isExpanded}
    >
      <AccordionSummary expandIcon={<ChevronIcon />}>
        <Stack direction="row" alignItems="center" gap="6px">
          <Styled.AccordionTitle isDisabled={isDisabled}>{displayTitle.toUpperCase()}</Styled.AccordionTitle>
          {count && (
            <span
              style={{
                background: "rgba(156,211,253,0.15)",
                color: "#9cd3fd",
                padding: "2px 8px",
                borderRadius: "10px",
                fontSize: "0.6875rem",
                fontWeight: 600,
                fontFamily:
                  '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                lineHeight: "1.4",
              }}
            >
              {count}
            </span>
          )}
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <Stack>{children}</Stack>
      </AccordionDetails>
    </Styled.Accordion>
  );
};
